import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AdminAuthContext = createContext(null);

function decodeJwt(token) {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

function isTokenExpired(token) {
  const payload = decodeJwt(token);
  if (!payload) return true;
  if (!payload.exp) return false;
  return payload.exp * 1000 < Date.now();
}

// LoginResponseModel yalnızca token/refreshToken garanti ediyor — email/isim JWT
// claim'lerinden çıkarılır (data'da varsa o öncelikli).
function buildProfile(data) {
  const claims = decodeJwt(data.token) || {};
  const email =
    data.email || claims.email || claims.unique_name || claims.upn || claims.sub || '';
  const name = data.name || claims.name || claims.given_name || '';
  return { email, name };
}

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [adminToken, setAdminToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logoutReason, setLogoutReason] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    const stored = localStorage.getItem('admin_user');

    if (token && !isTokenExpired(token) && stored) {
      try {
        setAdminToken(token);
        setAdmin(JSON.parse(stored));
      } catch {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_refresh_token');
        localStorage.removeItem('admin_user');
      }
    } else if (token) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_refresh_token');
      localStorage.removeItem('admin_user');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      setAdminToken(null);
      setAdmin(null);
      setLogoutReason(e.detail?.reason || 'unauthorized');
    };
    window.addEventListener('admin-logout', handler);
    return () => window.removeEventListener('admin-logout', handler);
  }, []);

  const login = useCallback((data) => {
    // data = { token, refreshToken, ... } (BaseResponseModel<LoginResponseModel>.data)
    localStorage.setItem('admin_token', data.token);
    if (data.refreshToken) {
      localStorage.setItem('admin_refresh_token', data.refreshToken);
    }
    const profile = buildProfile(data);
    localStorage.setItem('admin_user', JSON.stringify(profile));
    setAdminToken(data.token);
    setAdmin(profile);
    setLogoutReason(null);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_refresh_token');
    localStorage.removeItem('admin_user');
    setAdminToken(null);
    setAdmin(null);
  }, []);

  const clearLogoutReason = useCallback(() => setLogoutReason(null), []);

  const isAuthenticated = !!admin && !!adminToken && !isTokenExpired(adminToken);

  return (
    <AdminAuthContext.Provider
      value={{ admin, adminToken, isAuthenticated, loading, login, logout, logoutReason, clearLogoutReason }}
    >
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error('useAdminAuth must be used within AdminAuthProvider');
  return ctx;
};

export default AdminAuthContext;
